<template>
  <div
    :id="clickable ? buttonId : undefined"
    class="overflow-hidden rounded-3xl border border-solid border-gray-500 bg-white transition-shadow tablet:flex"
    :class="[
      clickable &&
        'group cursor-pointer hover:shadow-card hover:shadow-blue-400_a3',
      hasShadow && 'shadow-card',
    ]"
    :title="clickable ? title : ''"
    @click="handleClick"
  >
    <div
      class="relative flex shrink-0 flex-col justify-between gap-2 overflow-hidden p-4 tablet:w-1/3 tablet:p-5"
    >
      <div class="flex items-center justify-between gap-2">
        <img
          v-if="hasQuoteIcon"
          src="@/assets/images/quote-open.svg"
          alt="quote"
          class="pointer-events-none block h-8 w-8 flex-shrink-0 basis-8"
        />
        <div
          v-if="tag"
          class="truncate rounded bg-gray-600_a15 px-2.5 py-1 text-sm"
        >
          {{ tag }}
        </div>
      </div>

      <h3 class="text-h5 tablet:line-clamp-7">
        {{ title }}
      </h3>

      <p
        v-if="description"
        class="w-full truncate text-sm leading-6 text-gray-600"
      >
        {{ description }}
      </p>

      <TheNuxtLink
        v-if="buttonUrl"
        :id="!clickable ? buttonId : undefined"
        ref="linkRef"
        :to="buttonUrl"
        :title="!clickable ? buttonName : ''"
        class="hidden"
      />
    </div>

    <TheMedia
      :title="title"
      :description="description || title"
      :video-data="videoData"
      :thumbnail="imageUrl"
      :has-arrow-button="true"
      :theme="theme"
      class="aspect-video w-full tablet:aspect-[4/3] tablet:w-2/3 desktop:aspect-video"
    />
  </div>
</template>

<script setup lang="ts">
  import type { RouteLocationRaw } from 'vue-router';

  import type { IVideoData } from '@/components/Shared/Lightbox/Video.vue';
  import TheNuxtLink from '@/components/TheNuxtLink.vue';

  interface IProps {
    title: string;
    description?: string;
    buttonId?: string;
    buttonUrl?: RouteLocationRaw;
    buttonName?: string;
    tag?: string;
    imageUrl: string;
    videoUrl?: string;
    clickable?: boolean;
    hasShadow?: boolean;
    hasQuoteIcon?: boolean;
    theme?: TTheme;
  }

  const props = withDefaults(defineProps<IProps>(), {
    description: undefined,
    buttonId: undefined,
    buttonUrl: undefined,
    buttonName: undefined,
    tag: undefined,
    videoUrl: undefined,
    clickable: false,
    hasShadow: true,
    hasQuoteIcon: true,
    theme: 'blue',
  });

  const videoData = computed((): IVideoData | undefined =>
    props.videoUrl
      ? {
          title: props.title,
          description: props.description || props.title,
          url: props.videoUrl,
          imageUrl: props.imageUrl,
          buttonPosition: 'center',
        }
      : undefined
  );

  const linkRef = ref<InstanceType<typeof TheNuxtLink> | null>(null);

  const handleClick = (event: MouseEvent): void => {
    const linkEl = linkRef.value?.$el as HTMLElement | undefined;
    const isOutsideLink = linkEl && !linkEl.contains(event.target as Node);
    // 避免點擊連結本身時再次觸發，造成無窮迴圈
    if (props.clickable && props.buttonUrl && isOutsideLink) {
      linkEl.click();
    }
  };
</script>
